import React, {
  FunctionComponent,
  useCallback,
  useState,
  type JSX,
} from 'react';

import { useFieldId } from '@payaca/hooks/useFieldId';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import FieldLabel, { LabelStyleVariant } from '../fieldLabel/FieldLabel';
import InputWrapper, {
  InputStyleVariant,
  InputWidth,
} from '../inputWrapper/InputWrapper';

import './BasicField.sass';

export enum LabelPositionVariant {
  STACKED = 'label-stacked',
  INLINE = 'label-inline',
}

type Props = {
  className?: string;
  name: string;
  styleVariant?: InputStyleVariant;
  inputWidth?: InputWidth;
  labelStyleVariant?: LabelStyleVariant;
  value?: any;
  label?: string | JSX.Element;
  description?: string | JSX.Element;
  type?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  iconBefore?: TIconName | JSX.Element;
  iconAfter?: TIconName | JSX.Element;
  additionalInputProps?: { [key: string]: any };
  changeTimeoutMs?: number;
  onChange?: (value: { [key: string]: string }) => void;
  onTouch?: (fieldName: string) => void;
  onBlur?: (evt: React.FocusEvent<HTMLInputElement>) => void;
  onChangeTimeout?: () => void;
  textBefore?: string;
  hideSpinnerArrows?: boolean;
  placeholder?: string;
  onKeyPress?: (evt: React.KeyboardEvent<HTMLInputElement>) => void;
  bindEventHandlers?: (eventHandlers: {
    insertText: (text: string) => void;
  }) => void;
  labelPosition?: LabelPositionVariant;
};

const BasicField: FunctionComponent<React.PropsWithChildren<Props>> = ({
  className,
  name,
  styleVariant,
  inputWidth,
  labelStyleVariant,
  value,
  label,
  description,
  type = 'text',
  isDisabled = false,
  isRequired = false,
  iconBefore,
  iconAfter,
  additionalInputProps = {},
  changeTimeoutMs = 1000,
  onChange,
  onTouch,
  onBlur,
  onChangeTimeout,
  textBefore,
  hideSpinnerArrows = false,
  placeholder,
  onKeyPress,
  bindEventHandlers,
  labelPosition = LabelPositionVariant.STACKED,
}) => {
  const [changeTimeout, setChangeTimeout] = useState<any>();

  const id = useFieldId(name);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onChange &&
        onChange({
          [event.target.name]: event.target.value,
        });

      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      changeTimeout && clearTimeout(changeTimeout);
      setChangeTimeout(
        setTimeout(() => {
          // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
          onChangeTimeout && onChangeTimeout();
        }, changeTimeoutMs)
      );
    },
    [onChange, onChangeTimeout, changeTimeout, changeTimeoutMs]
  );

  const onInputTouch = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onTouch && onTouch(event.target.name);
    },
    [onTouch]
  );

  const handleBindEventHandlers = (target: HTMLInputElement) => {
    bindEventHandlers?.({
      insertText: (textToInsert: string) => {
        const cursorPosition = target.selectionStart || 0;
        const textBeforeCursorPosition = target.value.substring(
          0,
          cursorPosition
        );
        const textAfterCursorPosition = target.value.substring(
          cursorPosition,
          target.value.length
        );

        // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
        onChange &&
          onChange({
            [target.name]:
              textBeforeCursorPosition + textToInsert + textAfterCursorPosition,
          });

        // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
        changeTimeout && clearTimeout(changeTimeout);
        setChangeTimeout(
          setTimeout(() => {
            // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
            onChangeTimeout && onChangeTimeout();
          }, changeTimeoutMs)
        );
      },
    });
  };

  return (
    <div
      className={`basic-field ${className ? className : ''}  ${
        hideSpinnerArrows ? 'hide-spinner-arrows' : ''
      } ${labelPosition}`}
    >
      <FieldLabel
        label={label}
        id={id}
        isRequired={isRequired}
        description={description}
        styleVariant={labelStyleVariant}
      />
      <InputWrapper
        isDisabled={isDisabled}
        inputWidth={inputWidth}
        styleVariant={styleVariant}
      >
        <>
          {iconBefore &&
            (React.isValidElement(iconBefore) ? (
              <div className={`input-wrapper__pre-icon`}>{iconBefore}</div>
            ) : (
              <UntitledIcon
                className={`input-wrapper__pre-icon`}
                // @ts-expect-error wrong type
                name={iconBefore}
              />
            ))}
          {textBefore && (
            <span className={`input-wrapper__pre-text`}>{textBefore}</span>
          )}
          <input
            value={value}
            name={name}
            id={id}
            type={type}
            onChange={onInputChange}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
              onInputTouch && onInputTouch(event);
              // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
              onBlur && onBlur(event);
            }}
            onKeyPress={onKeyPress}
            disabled={isDisabled}
            className={`input-wrapper__input`}
            {...additionalInputProps}
            placeholder={placeholder}
            onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const target = e.target as HTMLInputElement;
              handleBindEventHandlers(target);
            }}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              const target = e.target as HTMLInputElement;
              handleBindEventHandlers(target);
            }}
          />
          {iconAfter &&
            (React.isValidElement(iconAfter) ? (
              <div className={`input-wrapper__post-icon`}>{iconAfter}</div>
            ) : (
              <UntitledIcon
                className={`input-wrapper__post-icon`}
                // @ts-expect-error
                name={iconAfter}
              />
            ))}
        </>
      </InputWrapper>
    </div>
  );
};

export default BasicField;
